import React, { useState, useEffect, useMemo } from 'react';
import { couponService } from '../services/couponService';
import { useLocation } from '../contexts/LocationContext';
import { useWarehouse } from '../contexts/WarehouseContext';
import { usePermissions } from '../contexts/PermissionsContext';
import SimpleUseCouponModal from '../components/coupons/SimpleUseCouponModal';
import SimpleHistoryModal from '../components/coupons/SimpleHistoryModal';
import { statusKuponu, statystykiKuponow, ETYKIETA_STATUSU } from '../utils/kuponStatus';
import ManualReceiptModal from '../components/coupons/ManualReceiptModal';
import CouponPurchaseDocumentModal from '../components/coupons/CouponPurchaseDocumentModal';
import AddCouponModal from '../components/coupons/AddCouponModal';
import ActionMenu from '../components/common/ActionMenu';

const CouponsPageSimple = () => {
  const { selectedLocation, locationId } = useLocation();
  const { selectedWarehouse, warehouseId } = useWarehouse();
  const { hasPermission } = usePermissions();

  const currentLocationId = locationId || warehouseId;

  // Granularne uprawnienia kupony
  const canCreateCoupon = hasPermission('kupony', 'tworzenie', currentLocationId);
  const canUseCoupon = hasPermission('kupony', 'realizacja', currentLocationId);

  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [stats, setStats] = useState({
    active_count: 0,
    active_value: 0,
    expired_count: 0,
    expired_value: 0,
    used_count: 0,
    used_value: 0,
    total_count: 0
  });
  const [showUseModal, setShowUseModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  
  // Stany sortowania
  const [sortColumn, setSortColumn] = useState('kod');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Funkcja sortowania
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Komponent ikony sortowania
  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <span style={{ opacity: 0.3, marginLeft: '4px' }}>↕</span>;
    }
    return <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  useEffect(() => {
    fetchCoupons();
  }, [currentLocationId]);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await couponService.getCoupons(currentLocationId);
      const couponsData = response?.data || [];
      setCoupons(Array.isArray(couponsData) ? couponsData : []);
      
      // Karty po terminie NIE sa aktywne (kasa je odrzuca) - liczone osobno jako 'wygasle'.
      setStats(statystykiKuponow(couponsData));
      setError(null);
    } catch (err) {
      setError('Błąd podczas pobierania kuponów');
      console.error('Fetch coupons error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredCoupons = useMemo(() => {
    const filtered = coupons.filter(coupon => {
      const matchesSearch = coupon.kod.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (coupon.kod_zewnetrzny && coupon.kod_zewnetrzny.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (coupon.numer_telefonu && coupon.numer_telefonu.includes(searchTerm));
      
      const st = statusKuponu(coupon);
      if (activeFilter === 'active') return matchesSearch && st === 'aktywny';
      if (activeFilter === 'expired') return matchesSearch && st === 'wygasly';
      if (activeFilter === 'used') return matchesSearch && st === 'wykorzystany';
      return matchesSearch;
    });
    
    return filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortColumn) {
        case 'id':
          aVal = parseInt(a.id || 0);
          bVal = parseInt(b.id || 0);
          break;
        case 'kod':
          aVal = (a.kod || '').toLowerCase();
          bVal = (b.kod || '').toLowerCase();
          break;
        case 'kod_zewnetrzny':
          aVal = (a.kod_zewnetrzny || '').toLowerCase();
          bVal = (b.kod_zewnetrzny || '').toLowerCase();
          break;
        case 'wartosc':
          aVal = parseFloat(a.wartosc || 0);
          bVal = parseFloat(b.wartosc || 0);
          break;
        case 'status':
          aVal = (a.status || '').toLowerCase();
          bVal = (b.status || '').toLowerCase();
          break;
        case 'data_waznosci':
          aVal = new Date(a.data_waznosci || '1900-01-01').getTime();
          bVal = new Date(b.data_waznosci || '1900-01-01').getTime();
          break;
        case 'numer_telefonu':
          aVal = (a.numer_telefonu || '');
          bVal = (b.numer_telefonu || '');
          break;
        case 'numer_paragonu':
          aVal = (a.numer_paragonu || '');
          bVal = (b.numer_paragonu || '');
          break;
        default:
          aVal = '';
          bVal = '';
      }
      
      if (typeof aVal === 'string') {
        const comparison = aVal.localeCompare(bVal, 'pl');
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      
      if (sortDirection === 'asc') {
        return aVal - bVal;
      }
      return bVal - aVal;
    });
  }, [coupons, searchTerm, activeFilter, sortColumn, sortDirection]);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
        <div style={{ color: 'var(--text-muted)' }}>Ładowanie kuponów...</div>
      </div>
    );
  }

  return (
    <div style={{ fontSize: '12px' }}>
      {/* Header */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>Kupony podarunkowe</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              {selectedLocation?.nazwa || selectedWarehouse?.nazwa || 'Wszystkie lokalizacje'} | {new Date().toLocaleDateString()}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[
              { id: 'all', label: `Wszystkie (${stats.total_count})` },
              { id: 'active', label: `Aktywne (${stats.active_count})` },
              { id: 'expired', label: `Wygasłe (${stats.expired_count})` },
              { id: 'used', label: `Wykorzystane (${stats.used_count})` }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setActiveFilter(f.id)}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  backgroundColor: activeFilter === f.id ? 'var(--primary)' : 'var(--card-bg)',
                  border: activeFilter === f.id ? '1px solid var(--primary)' : '1px solid var(--border)',
                  color: activeFilter === f.id ? '#fff' : 'var(--text-secondary)',
                  fontWeight: activeFilter === f.id ? '600' : '500',
                  cursor: 'pointer'
                }}
              >
                {f.label}
              </button>
            ))}
            {canCreateCoupon && (
              <button
                onClick={() => setShowAddModal(true)}
                style={{
                  padding: '6px 12px', fontSize: '12px', fontWeight: '600',
                  backgroundColor: 'var(--primary)', border: '1px solid var(--primary)',
                  color: '#fff', cursor: 'pointer'
                }}
              >
                Dodaj kupon
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Błąd */}
      {error && (
        <div style={{
          padding: '0.75rem',
          backgroundColor: 'var(--danger-light)',
          color: 'var(--danger)',
          borderRadius: '0.375rem',
          marginBottom: '0.75rem',
          fontSize: '12px',
          border: '1px solid var(--danger-light)'
        }}>
          ❌ {error}
        </div>
      )}

      {/* Statystyki - kompaktowe karty */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '0.5rem',
        flexWrap: 'wrap'
      }}>
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '0.4rem 0.6rem',
          borderRadius: '4px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
          borderLeft: '3px solid var(--info)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>Wszystkich:</div>
          <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--primary)' }}>{stats.total_count}</div>
        </div>
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '0.4rem 0.6rem',
          borderRadius: '4px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
          borderLeft: '3px solid var(--success)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>Aktywne:</div>
          <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--success)' }}>{stats.active_count}</div>
          <div style={{ fontSize: '9px', color: 'var(--success)' }}>({stats.active_value.toFixed(2)} zł)</div>
        </div>
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '0.4rem 0.6rem',
          borderRadius: '4px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
          borderLeft: '3px solid var(--danger)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>Wykorzystane:</div>
          <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--danger)' }}>{stats.used_count}</div>
          <div style={{ fontSize: '9px', color: 'var(--danger)' }}>({stats.used_value.toFixed(2)} zł)</div>
        </div>
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '0.4rem 0.6rem',
          borderRadius: '4px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
          borderLeft: '3px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }} title="Karty po terminie ważności z niewykorzystanym saldem. Kasa ich nie przyjmuje.">
          <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>Wygasłe:</div>
          <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>{stats.expired_count}</div>
          <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>({stats.expired_value.toFixed(2)} zł)</div>
        </div>
        <div style={{
          backgroundColor: 'var(--card-bg)',
          padding: '0.4rem 0.6rem',
          borderRadius: '4px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
          borderLeft: '3px solid var(--warning)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <div style={{ fontSize: '9px', color: 'var(--text-muted)' }} title="Suma sald kart aktywnych i w terminie. Bez kart wygasłych i wykorzystanych.">Wartość aktywnych:</div>
          <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--warning)' }}>{stats.active_value.toFixed(2)} zł</div>
        </div>
      </div>

      {/* Wyszukiwarka */}
      <div style={{
        backgroundColor: 'var(--card-bg)',
        padding: '0.75rem',
        borderRadius: '0.375rem',
        marginBottom: '0.75rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '14px' }}>🔍</span>
          <input
            type="text"
            placeholder="Szukaj po kodzie kuponu lub numerze telefonu..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              padding: '6px 10px',
              fontSize: '12px',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              backgroundColor: 'var(--card-bg)',
              color: 'var(--text)',
              outline: 'none'
            }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                padding: '4px 10px',
                fontSize: '11px',
                backgroundColor: 'var(--text-muted)',
                color: 'white'
              }}
            >
              ✕ Wyczyść
            </button>
          )}
        </div>
      </div>

      {/* Lista kuponów - tabela w stylu POS */}
      <div style={{
        backgroundColor: 'var(--card-bg)',
        borderRadius: '0.375rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '0.75rem',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontWeight: '600', fontSize: '13px' }}>
            📋 Lista kuponów
            <span style={{ 
              marginLeft: '8px', 
              padding: '2px 8px', 
              border: '1px solid var(--primary)', color: 'var(--primary)', 
              borderRadius: '10px',
              fontSize: '11px'
            }}>
              {filteredCoupons.length}
            </span>
          </div>
          <button
            onClick={fetchCoupons}
            style={{
              padding: '4px 10px',
              fontSize: '11px',
              backgroundColor: 'var(--card-bg)',
              border: '1px solid var(--border)',
              color: 'var(--text-secondary)'
            }}
          >
            🔄 Odśwież
          </button>
        </div>

        {filteredCoupons.length === 0 ? (
          <div style={{ 
            padding: '2rem', 
            textAlign: 'center', 
            color: 'var(--text-muted)' 
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎟️</div>
            <div>Brak kuponów do wyświetlenia</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg)' }}>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none', width: '40px' }} onClick={() => handleSort('id')}>LP<SortIcon column="id" /></th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('kod')}>Kod<SortIcon column="kod" /></th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('kod_zewnetrzny')}>Kod zewn.<SortIcon column="kod_zewnetrzny" /></th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('wartosc')}>Wartość<SortIcon column="wartosc" /></th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>Status<SortIcon column="status" /></th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('data_waznosci')}>Ważność<SortIcon column="data_waznosci" /></th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('numer_telefonu')}>Telefon<SortIcon column="numer_telefonu" /></th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('numer_paragonu')}>Paragon<SortIcon column="numer_paragonu" /></th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', borderBottom: '2px solid var(--border)', fontWeight: '600', color: 'var(--text-secondary)' }}>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {filteredCoupons.map((coupon, index) => (
                  <tr 
                    key={coupon.id} 
                    style={{
                      backgroundColor: index % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)'
                    }}
                  >
                    <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)', textAlign: 'center', color: 'var(--text-secondary)' }}>
                      {index + 1}
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ 
                        fontWeight: '600', 
                        fontFamily: 'monospace',
                        backgroundColor: 'var(--border)',
                        padding: '2px 6px',
                        borderRadius: '3px'
                      }}>
                        {coupon.kod}
                      </span>
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)' }}>
                      {coupon.kod_zewnetrzny ? (
                        <span style={{ 
                          fontWeight: '500', 
                          fontFamily: 'monospace',
                          backgroundColor: 'var(--warning-light)',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontSize: '10px'
                        }}>
                          {coupon.kod_zewnetrzny}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)', textAlign: 'right', fontWeight: '600' }}>
                      {parseFloat(coupon.wartosc || 0).toFixed(2)} zł
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
                      <span style={{
                        padding: '3px 8px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: '600',
                        backgroundColor: statusKuponu(coupon) === 'aktywny' ? 'var(--success-light)'
                          : statusKuponu(coupon) === 'wygasly' ? 'var(--bg)' : 'var(--danger-light)',
                        color: statusKuponu(coupon) === 'aktywny' ? 'var(--success)'
                          : statusKuponu(coupon) === 'wygasly' ? 'var(--text-muted)' : 'var(--danger)',
                        border: statusKuponu(coupon) === 'wygasly' ? '1px solid var(--border)' : 'none'
                      }}
                      title={statusKuponu(coupon) === 'wygasly' ? `Termin ważności minął (${coupon.data_waznosci}). Kasa nie przyjmie tej karty, choć ma saldo.` : undefined}
                      data-testid={`status-kuponu-${coupon.id}`}>
                        {ETYKIETA_STATUSU[statusKuponu(coupon)]}
                      </span>
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)', textAlign: 'center', fontSize: '10px' }}>
                      {coupon.data_waznosci}
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)', fontSize: '10px' }}>
                      {coupon.numer_telefonu || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', position: 'relative' }}>
                        {coupon.paragony && coupon.paragony.length > 0 ? (
                          <div 
                            style={{ 
                              position: 'relative',
                              cursor: coupon.paragony.length > 1 ? 'pointer' : 'default'
                            }}
                            title={coupon.paragony.length > 1 ? `Kliknij aby zobaczyć wszystkie (${coupon.paragony.length})` : ''}
                            onClick={() => {
                              if (coupon.paragony.length > 1) {
                                setSelectedCoupon(coupon);
                                setShowHistoryModal(true);
                              }
                            }}
                          >
                            <span style={{ 
                              fontSize: '10px',
                              backgroundColor: 'var(--success-light)',
                              padding: '2px 6px',
                              borderRadius: '3px',
                              fontFamily: 'monospace'
                            }}>
                              {coupon.paragony[0].numer_paragonu || '—'}
                            </span>
                            {coupon.paragony.length > 1 && (
                              <span style={{
                                marginLeft: '4px',
                                fontSize: '9px',
                                border: '1px solid var(--primary)', color: 'var(--primary)',
                                padding: '1px 4px',
                                borderRadius: '8px',
                                fontWeight: '600'
                              }}>
                                +{coupon.paragony.length - 1}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span style={{ fontSize: '10px' }}>{coupon.numer_paragonu || <span style={{ color: 'var(--text-muted)' }}>—</span>}</span>
                        )}
                        <button
                          onClick={() => { setSelectedCoupon(coupon); setShowReceiptModal(true); }}
                          style={{
                            padding: '4px 10px',
                            fontSize: '11px',
                            backgroundColor: 'var(--card-bg)',
                            border: '1px solid var(--border)',
                            color: 'var(--text-secondary)'
                          }}
                        >
                          ✏️
                        </button>
                      </div>
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
                      <ActionMenu actions={[
                        statusKuponu(coupon) === 'aktywny' && canUseCoupon && { icon: '💳', label: 'Użyj', color: 'var(--primary)', onClick: () => { setSelectedCoupon(coupon); setShowUseModal(true); } },
                        { icon: '📄', label: 'Dokument', color: 'var(--success)', onClick: () => { setSelectedCoupon(coupon); setShowDocumentModal(true); } },
                        { icon: '📜', label: 'Historia', color: 'var(--text-muted)', onClick: () => { setSelectedCoupon(coupon); setShowHistoryModal(true); } },
                      ]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MODALE */}
      <AddCouponModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => { setShowAddModal(false); fetchCoupons(); }}
        locationId={currentLocationId}
        locationName={selectedLocation?.nazwa || selectedWarehouse?.nazwa}
      />
      <SimpleUseCouponModal
        coupon={selectedCoupon}
        isOpen={showUseModal}
        onClose={() => { setShowUseModal(false); setSelectedCoupon(null); }}
        onSuccess={fetchCoupons}
      />
      <SimpleHistoryModal
        couponId={selectedCoupon?.id}
        isOpen={showHistoryModal}
        onClose={() => { setShowHistoryModal(false); setSelectedCoupon(null); }}
      />
      <ManualReceiptModal
        coupon={selectedCoupon}
        isOpen={showReceiptModal}
        onClose={() => { setShowReceiptModal(false); setSelectedCoupon(null); }}
        onSuccess={fetchCoupons}
      />
      <CouponPurchaseDocumentModal
        couponId={selectedCoupon?.id}
        isOpen={showDocumentModal}
        onClose={() => { setShowDocumentModal(false); setSelectedCoupon(null); }}
      />
    </div>
  );
};

export default CouponsPageSimple;
